import { ENABLE_I18N } from '@src/config/env'
import {
  TAbsoluteLocale,
  TCountry,
  TCountryLanguage,
  LOCALE_FORMAT,
  DEFAULT_COUNTRY,
  TAbsoluteLocaleStandard,
  TCountryLanguageStandard,
  TCountryStandard,
} from '@src/config/i18n.config'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import {
  customerServiceClient,
  reportingServicesHttpClient,
  storefrontRegionsHttpClient,
} from '@src/quince-site-common/src/utils/HttpClient'

import { parseFormat } from './general.utils'
export const parseLocale = (
  locale: TAbsoluteLocale | TAbsoluteLocaleStandard,
): { country: TCountry; language: TCountryLanguage<TCountry> } => {
  return parseFormat(LOCALE_FORMAT, locale) as {
    country: TCountry
    language: TCountryLanguage<TCountry>
  }
}

export const getStandardLocale = (locale: TAbsoluteLocale | TAbsoluteLocaleStandard): TAbsoluteLocaleStandard => {
  const parsedLocale = parseLocale(locale)
  return `${parsedLocale.language}-${parsedLocale.country.toUpperCase()}` as TAbsoluteLocaleStandard
}

export const getQuinceLocale = (locale: TAbsoluteLocale | TAbsoluteLocaleStandard) => {
  return locale.toLowerCase() as TAbsoluteLocale
}

export const getShortenedLocale = (locale: TAbsoluteLocale, regionDetails: TRegionDetail) => {
  const { country } = parseLocale(locale)
  if (locale === regionDetails.regions[DEFAULT_COUNTRY]?.defaultLanguageIsoCode) return ''
  if (locale === regionDetails.regions[country]?.defaultLanguageIsoCode) return country
  return locale
}

/**
 * Returns the value associated with the primaryKey if it exists in the object.
 * Otherwise, it returns the value associated with the fallbackKey.
 *
 * @param {T} obj - The object to search for the keys.
 * @param {K} primaryKey - The primary key to look for in the object.
 * @param {L} fallbackKey - The fallback key to use if the primary key is not found.
 * @returns {T[K]} - The value associated with the primaryKey or fallbackKey.
 */
export function getDisplayKey<T, K extends keyof T, L>(obj: T, primaryKey: K, fallback: L) {
  if (!ENABLE_I18N) return fallback
  if (typeof obj[primaryKey] === 'string' && obj[primaryKey] === '') return fallback
  return obj[primaryKey] ?? fallback
}

export interface TSupportedLanguage {
  isoCode: TAbsoluteLocale
  displayName: string
}

export interface TResponseRegionDetail {
  regionId: number
  regionCode: TCountry
  internalName: string
  displayName: string
  defaultLanguageIsoCode: TAbsoluteLocale
  defaultCurrencyIsoCode: string
  supportedLanguagesIsoCodes: TAbsoluteLocale[]
  supportedLanguages: TSupportedLanguage[]
}

export interface TRegionDetail {
  regionCodes: TCountry[]
  regions: Partial<
    Record<
      TCountry,
      Omit<TResponseRegionDetail, 'supportedLanguages'> & {
        supportedLanguages: Record<TAbsoluteLocale, TSupportedLanguage>
      }
    >
  >
}

export const fetchRegionDetails = async ({
  shouldFetch = false,
}: {
  shouldFetch?: boolean
} = {}): Promise<TRegionDetail> => {
  if (!ENABLE_I18N && !shouldFetch)
    return {
      regionCodes: [],
      regions: {},
    }
  try {
    const res = await storefrontRegionsHttpClient.get(`/regions/all/lite?options=ENRICH_SUPPORTED_LANGUAGES`)
    const regionDetails = (await res.data) as TResponseRegionDetail[]

    const regions = regionDetails.reduce(
      (acc, region) => ({
        ...acc,
        [region.regionCode]: {
          ...region,
          supportedLanguages: region.supportedLanguages.reduce(
            (acc, supportedLanguage) => ({
              ...acc,
              [supportedLanguage.isoCode]: supportedLanguage,
            }),
            {},
          ),
        },
      }),
      {},
    )

    const updatedRegionsData = {
      regionCodes: regionDetails.map(region => region.regionCode),
      regions,
    }

    return transformToLowerCase(updatedRegionsData)
  } catch (err) {
    return {
      regionCodes: [],
      regions: {},
    }
  }
}

export const updateCommunicationPreference = async ({
  locale,
  regionDetails,
  abortController,
}: {
  locale: TAbsoluteLocaleStandard
  regionDetails: TRegionDetail
  abortController: AbortController | null
}) => {
  const country = parseLocale(locale).country
  const regionId = regionDetails.regions[country.toLowerCase() as TCountry]?.regionId // as regionDetails is in lowercase we need to convert the country to lowercase but comm API expects data in uppercase
  const loggedIn = AuthUtil.isLoggedIn()
  if (!regionId || !loggedIn) return

  try {
    const response = await customerServiceClient.post(
      '/settings',
      {
        preferredLanguage: locale,
        regionId,
      },
      {
        signal: abortController?.signal,
      },
    )
    return response
  } catch (error) {
    if (abortController?.signal.aborted) return
    console.error('Failed to update locale settings: ', error)
    throw new QuinceApiError({
      error: error as Error,
      message: 'Failed to update locale settings',
      apiName: 'settings',
    })
  }
}

export const getPreferredLanguage = async () => {
  try {
    const res = (await reportingServicesHttpClient.get('/customers/meta')) as {
      data: {
        preferredLanguage: TCountryLanguageStandard<TCountryStandard> | null
      }
    }
    return res.data.preferredLanguage
  } catch (error) {
    console.error('Failed to get preferred language: ', error)
    return null
  }
}

export const getAbsoluteLocale = (
  locale: TCountry | TAbsoluteLocale | '',
  regionDetails: TRegionDetail,
): TAbsoluteLocale => {
  if (locale === '') {
    return regionDetails.regions[DEFAULT_COUNTRY]?.defaultCurrencyIsoCode as TAbsoluteLocale
  }
  if (Object.hasOwn(regionDetails.regions, locale)) {
    return regionDetails.regions[locale as TCountry]?.defaultLanguageIsoCode as TAbsoluteLocale
  }
  return locale as TAbsoluteLocale
}

/* 
  This function is used to transform the region details to lowercase.
  This is because all downstream services stores in region details and locale in en-US format.
  This was is done to support lowercase locale across website urls quince.com/en-us/products/all
  Please ask sachin@onequince.com for more details.
*/
export function transformToLowerCase(data: TRegionDetail): TRegionDetail {
  // Create a new object to store transformed data
  const transformedData: TRegionDetail = {
    // Transform regionCodes array to lowercase
    regionCodes: data.regionCodes.map(code => code.toLowerCase() as TCountry),
    regions: {},
  }

  // Transform regions object
  Object.entries(data.regions).forEach(([regionKey, region]) => {
    // Create lowercase version of region key
    const lowerRegionKey = regionKey.toLowerCase() as TCountry

    // Transform the region data
    const transformedRegion = {
      ...region,
      regionCode: region.regionCode.toLowerCase() as TCountry,
      defaultLanguageIsoCode: region.defaultLanguageIsoCode.toLowerCase() as TAbsoluteLocale,
      supportedLanguagesIsoCodes: region.supportedLanguagesIsoCodes.map(code => code.toLowerCase() as TAbsoluteLocale),
      supportedLanguages: {} as Record<TAbsoluteLocale, TSupportedLanguage>,
    }

    // Transform supportedLanguages
    Object.entries(region.supportedLanguages).forEach(([langKey, language]) => {
      const lowerLangKey = langKey.toLowerCase() as TAbsoluteLocale
      transformedRegion.supportedLanguages[lowerLangKey] = {
        ...language,
        isoCode: language.isoCode.toLowerCase() as TAbsoluteLocale,
      }
    })

    // Add transformed region to new object using lowercase key
    transformedData.regions[lowerRegionKey] = transformedRegion
  })

  return transformedData
}

//make this dynamic from the region details
export const allUrlLocales = ['us', 'en-us', 'ca', 'fr-ca', 'en-ca'] // eslint-disable-line i18next/no-literal-string

const removeLocaleFromUrl = (pathnameWithSearchParams: string) => {
  const potentialLocale = pathnameWithSearchParams.split('/')[2] || ''
  console.log('test1 potentialLocale :>> ', potentialLocale)
  if (allUrlLocales.includes(potentialLocale)) {
    console.log('test2 pathnameWithSearchParams.split :>> ', pathnameWithSearchParams.split('/'))
    return pathnameWithSearchParams.split('/').slice(2).join('/')
  }
  return pathnameWithSearchParams
}

export const handleLocaleForUrl = (updatedLocale: string, pathnameWithSearchParams: string) => {
  if (updatedLocale) {
    const finalUrl = '/' + updatedLocale + removeLocaleFromUrl(pathnameWithSearchParams)
    window.location.href = window.location.origin + finalUrl
  } else {
    window.location.href = window.location.origin + removeLocaleFromUrl(pathnameWithSearchParams)
  }
}
